import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Confirm } from 'notiflix/build/notiflix-confirm-aio'
import api, { apiDownload, apiUpload } from '../../../helpers/apiRequester'
import { Banner, Button, Card, Checkbox, CollapsibleSection, EmptyState, Field, FileInput, Input, Modal, PillTone, ProgressBar, SegmentedControl, Spinner, StatCard, StatGrid, StatusPill } from '../../../ui'
import styles from './styles.module.scss'

type BatchStatus = 'draft' | 'approved' | 'sending' | 'sent' | 'partially_sent' | 'cancelled'
type JobType = 'render' | 'send'
type JobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

interface Batch {
    id: string
    name: string
    source_db: string | null
    source_query_ref: string | null
    total_claims: number
    status: BatchStatus
    created_by: string
    approved_by: string | null
    approved_at: string | null
    created_at: string
    updated_at: string
}

interface StatusCount {
    status: string
    count: number
}

interface Job {
    id: string
    batch_id: string
    job_type: JobType
    status: JobStatus
    created_by: string | null
    total: number
    processed: number
    succeeded: number
    failed: number
    report: any
    last_error: string | null
    started_at: string | null
    finished_at: string | null
    created_at: string
    percent: number
}

interface PreviewRow {
    id: string
    claimRef: string
    clientRef: string | null
    lenderName: string | null
    toEmail: string
    subject: string
    footerRef: string
    status: string
    lastError: string | null
    rendered: boolean
    suppressed: boolean
    suppressionReason: string | null
    suppressionNotes: string | null
    recoverable: boolean
    duplicateOfSendId: string | null
    originalBatchName: string | null
    originalSentAt: string | null
}

interface ImportInvalidRow {
    rowNum: number
    claimRef?: string
    reason: string
}

interface ImportRefRow {
    rowNum: number
    claimRef: string
}

interface ImportAlreadyInDbRow {
    rowNum: number
    claimRef: string
    originalBatchName?: string | null
    originalSentAt?: string | null
}

interface ImportSuppressedRow {
    rowNum: number
    claimRef: string
    email: string
}

interface ImportSkippedRow {
    rowNum: number
    claimRef: string
    originalBatchName: string | null
    originalSentAt: string | null
    originalSendId: string | null
}

interface ImportReportCounts {
    queue?: number
    invalid?: number
    duplicateInFile?: number
    alreadyInDb?: number
    skippedDuplicates?: number
    suppressedWarnings?: number
    unknownLenders?: number
}

interface ImportReport {
    batchId: string
    batchName: string
    sourceFile?: string
    sheetName?: string
    totalRows?: number
    queued: number
    written: boolean
    counts: ImportReportCounts
    invalid: ImportInvalidRow[]
    duplicateInFile: ImportRefRow[]
    alreadyInDb: ImportAlreadyInDbRow[]
    skippedDuplicates?: ImportSkippedRow[]
    suppressedWarnings: ImportSuppressedRow[]
    unknownLenders: string[]
}

const DELETABLE_BATCH_STATUSES: BatchStatus[] = ['draft', 'approved', 'cancelled']

const POLL_INTERVAL_MS = 2000

const RETRYABLE_STATUSES = ['failed', 'bounced', 'suppressed']
const CANCELLABLE_STATUSES = ['queued', 'failed', 'bounced', 'suppressed']
const EDITABLE_STATUSES = ['queued', 'bounced', 'failed', 'suppressed']

const FAILURE_STATUSES = ['failed', 'bounced', 'complained', 'suppressed']

type FailureFilter =
    | 'all'
    | 'failures'
    | 'notRendered'
    | 'suppressed'
    | 'cancelled'
    | 'skippedDuplicate'

type PreviewFilter = FailureFilter | `status:${string}`

const FILTER_CHIPS: { key: FailureFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'failures', label: 'Failures' },
    { key: 'notRendered', label: 'Not rendered' },
    { key: 'suppressed', label: 'Suppressed' },
    { key: 'cancelled', label: 'Cancelled' },
    { key: 'skippedDuplicate', label: 'Skipped' },
]

const matchesFilter = (row: PreviewRow, filter: PreviewFilter): boolean => {
    if (filter === 'all') return true
    if (filter.startsWith('status:')) return row.status === filter.slice('status:'.length)
    switch (filter) {
        case 'failures':
            return (
                FAILURE_STATUSES.includes(row.status) ||
                (row.status === 'queued' && !!row.lastError && !row.rendered)
            )
        case 'notRendered':
            return row.status === 'queued' && !row.rendered && !row.lastError
        case 'suppressed':
            return row.suppressed === true
        case 'cancelled':
            return row.status === 'cancelled'
        case 'skippedDuplicate':
            return row.status === 'skipped_duplicate'
        default:
            return true
    }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type AttachmentKind = 'cover' | 'foa' | 'loc'

const ATTACHMENT_LINKS: { kind: AttachmentKind; label: string }[] = [
    { kind: 'cover', label: 'Cover' },
    { kind: 'foa', label: 'FOA' },
    { kind: 'loc', label: 'LOC' },
]

const formatDateTime = (raw: string | null | undefined) => {
    if (!raw) return '—'
    try {
        return new Date(raw).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    } catch {
        return raw
    }
}

const isJobActive = (job: Job | null | undefined): job is Job =>
    !!job && (job.status === 'pending' || job.status === 'running')

const ImportRejectSections = ({ report }: { report: ImportReport }) => {
    const sections: {
        key: string
        label: string
        tone: 'bad' | 'warn' | 'muted'
        note: string
        rows: {
            rowNum: number
            claimRef?: string
            reason: string
            extra?: string
        }[]
    }[] = [
        {
            key: 'invalid',
            label: 'Invalid',
            tone: 'bad',
            note: 'Missing required data, unknown lender / introducer, or bad email format — these rows were not queued.',
            rows: report.invalid.map((r) => ({
                rowNum: r.rowNum,
                claimRef: r.claimRef,
                reason: r.reason,
            })),
        },
        {
            key: 'duplicateInFile',
            label: 'Duplicates in file',
            tone: 'warn',
            note: 'The same claim reference appears more than once in this spreadsheet — only the first was kept.',
            rows: report.duplicateInFile.map((r) => ({
                rowNum: r.rowNum,
                claimRef: r.claimRef,
                reason: 'duplicate claim ref within this file',
            })),
        },
        {
            key: 'alreadyInDb',
            label: 'Already in DB',
            tone: 'warn',
            note: 'These claim refs already exist in a previous batch. They now appear in the batch below as "skipped (duplicate)" rows so you can see them — they will never send. Tick "Allow resend" on the import panel to send them again.',
            rows: report.alreadyInDb.map((r) => ({
                rowNum: r.rowNum,
                claimRef: r.claimRef,
                reason: r.originalBatchName
                    ? `already sent in "${r.originalBatchName}"${
                          r.originalSentAt ? ` on ${formatDateTime(r.originalSentAt)}` : ''
                      }`
                    : 'claim ref already exists in a prior batch',
            })),
        },
        {
            key: 'skippedDuplicates',
            label: 'Skipped (duplicate)',
            tone: 'muted',
            note: 'Added to the batch as non-sending rows so they stay visible. Each links back to the original send.',
            rows: (report.skippedDuplicates ?? []).map((r) => ({
                rowNum: r.rowNum,
                claimRef: r.claimRef,
                reason: r.originalBatchName
                    ? `already sent in "${r.originalBatchName}"${
                          r.originalSentAt ? ` on ${formatDateTime(r.originalSentAt)}` : ''
                      }`
                    : 'already sent in a prior batch',
            })),
        },
        {
            key: 'suppressedWarnings',
            label: 'Suppressed (queued anyway)',
            tone: 'muted',
            note: 'These were queued but the lender address is on the suppression list — they will not send until suppression is cleared.',
            rows: report.suppressedWarnings.map((r) => ({
                rowNum: r.rowNum,
                claimRef: r.claimRef,
                reason: `suppression on ${r.email}`,
                extra: r.email,
            })),
        },
    ]

    const anyRows = sections.some((s) => s.rows.length > 0)
    if (!anyRows) {
        return <Banner tone="good">No rows were rejected — every row in your file made it through.</Banner>
    }

    return (
        <div className={styles.stack}>
            {sections
                .filter((section) => section.rows.length > 0)
                .map((section) => (
                    <CollapsibleSection key={section.key} title={section.label} meta={`${section.rows.length}`} variant="panel" tone={section.tone === 'bad' ? 'bad' : 'default'} defaultOpen={section.tone === 'bad' && section.rows.length <= 100}>
                        <p className={styles.rejectNote}>{section.note}</p>
                        <div className={styles.importRejectTableWrapper}>
                            <table className={styles.importRejectTable}>
                                <thead>
                                    <tr>
                                        <th>Row #</th>
                                        <th>Claim ref</th>
                                        <th>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {section.rows.map((row, idx) => (
                                        <tr key={`${row.rowNum}-${row.claimRef ?? idx}`}>
                                            <td className={styles.mono}>{row.rowNum}</td>
                                            <td className={styles.mono}>{row.claimRef ?? '—'}</td>
                                            <td>{row.reason}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CollapsibleSection>
                ))}
        </div>
    )
}

const BATCH_STATUS_TONE: Record<BatchStatus, PillTone> = {
    draft: 'neutral',
    approved: 'info',
    sending: 'warn',
    sent: 'good',
    partially_sent: 'warn',
    cancelled: 'bad',
}

const BatchStatusBadge = ({ status }: { status: BatchStatus }) => (
    <StatusPill tone={BATCH_STATUS_TONE[status]} size="sm" dot>
        {status.replace('_', ' ')}
    </StatusPill>
)

export const CommsLocBatchesView = () => {
    const [batches, setBatches] = useState<Batch[]>([])
    const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null)
    const [batch, setBatch] = useState<Batch | null>(null)
    const [statusCounts, setStatusCounts] = useState<StatusCount[]>([])
    const [activeJob, setActiveJob] = useState<Job | null>(null)
    const [previewRows, setPreviewRows] = useState<PreviewRow[]>([])
    const [filter, setFilter] = useState<PreviewFilter>('all')

    const [listLoading, setListLoading] = useState(true)
    const [detailLoading, setDetailLoading] = useState(false)
    const [actionPending, setActionPending] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [viewingKey, setViewingKey] = useState<string | null>(null)
    const [rowActionKey, setRowActionKey] = useState<string | null>(null)
    const [editModal, setEditModal] = useState<{ row: PreviewRow; lenderCount: number } | null>(null)
    const [editEmailValue, setEditEmailValue] = useState('')

    const [importFile, setImportFile] = useState<File | null>(null)
    const [importName, setImportName] = useState('')
    const [allowResend, setAllowResend] = useState(false)
    const [importing, setImporting] = useState(false)
    const [importReport, setImportReport] = useState<ImportReport | null>(null)
    const [importReportExpanded, setImportReportExpanded] = useState(true)
    const [copiedUnknownLenders, setCopiedUnknownLenders] = useState(false)

    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const graceTimersRef = useRef<ReturnType<typeof setTimeout>[]>([])
    const fileInputRef = useRef<HTMLInputElement | null>(null)

    const clearGraceTimers = useCallback(() => {
        graceTimersRef.current.forEach((t) => clearTimeout(t))
        graceTimersRef.current = []
    }, [])

    const stopPolling = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
        clearGraceTimers()
    }, [clearGraceTimers])

    const loadBatches = useCallback(async () => {
        try {
            const resp = await api({ url: 'comms/loc-batches/list', data: {} })
            if (resp.data?.success) setBatches(resp.data.batches || [])
        } catch (err: any) {
            Report.failure(
                'Load failed',
                err?.response?.data?.message || 'Could not load batches. Please try again.',
                'Okay'
            )
        }
    }, [])

    const loadDetail = useCallback(async (batchId: string, opts: { showSpinner?: boolean } = {}) => {
        const { showSpinner = false } = opts
        if (showSpinner) setDetailLoading(true)
        try {
            const [getResp, previewResp] = await Promise.all([
                api({ url: 'comms/loc-batches/get', data: { batchId } }),
                api({ url: 'comms/loc-batches/preview', data: { batchId } }),
            ])
            if (getResp.data?.success) {
                setBatch(getResp.data.batch || null)
                setStatusCounts(getResp.data.statusCounts || [])
                setActiveJob(getResp.data.activeJob || null)
            }
            if (previewResp.data?.success) {
                setPreviewRows(previewResp.data.rows || [])
            }
            return getResp.data?.activeJob || null
        } catch (err: any) {
            Report.failure(
                'Load failed',
                err?.response?.data?.message || 'Could not load batch detail.',
                'Okay'
            )
            return null
        } finally {
            if (showSpinner) setDetailLoading(false)
        }
    }, [])

    const startPolling = useCallback(
        (jobId: string, batchId: string) => {
            stopPolling()
            pollRef.current = setInterval(async () => {
                try {
                    const resp = await api({ url: 'comms/loc-batches/job-get', data: { jobId } })
                    if (!resp.data?.success) return
                    const job: Job = resp.data.job
                    setActiveJob(job)
                    if (job.status === 'completed' || job.status === 'failed' || job.status === 'cancelled') {
                        stopPolling()
                        await loadDetail(batchId)
                        await loadBatches()
                        if (job.job_type === 'send') {
                            clearGraceTimers()
                            for (const delay of [3000, 8000]) {
                                graceTimersRef.current.push(
                                    setTimeout(() => {
                                        loadDetail(batchId)
                                        loadBatches()
                                    }, delay)
                                )
                            }
                        }
                        if (job.status === 'completed') {
                            Report.success(
                                `${job.job_type === 'render' ? 'Render' : 'Send'} complete`,
                                `Processed ${job.processed} of ${job.total}. ` +
                                    `Succeeded: ${job.succeeded}, Failed: ${job.failed}.`,
                                'Okay'
                            )
                        } else if (job.status === 'failed') {
                            Report.failure(
                                `${job.job_type === 'render' ? 'Render' : 'Send'} failed`,
                                (job.last_error || 'The job failed.') +
                                    ' If any LOC emails are stuck in "sending", use the "Recover stuck" button above to re-queue them.',
                                'Okay'
                            )
                        }
                    }
                } catch (err) {
                    console.error('[LOC Batch] poll failed:', err)
                }
            }, POLL_INTERVAL_MS)
        },
        [stopPolling, loadDetail, loadBatches, clearGraceTimers]
    )

    useEffect(() => {
        ;(async () => {
            await loadBatches()
            setListLoading(false)
        })()
        return () => stopPolling()
    }, [loadBatches, stopPolling])

    const handleSelectBatch = useCallback(
        async (batchId: string) => {
            if (batchId === selectedBatchId) return
            stopPolling()
            setSelectedBatchId(batchId)
            setBatch(null)
            setStatusCounts([])
            setActiveJob(null)
            setPreviewRows([])
            if (importReport && importReport.batchId !== batchId) setImportReport(null)
            const job = await loadDetail(batchId, { showSpinner: true })
            if (isJobActive(job)) startPolling(job.id, batchId)
        },
        [selectedBatchId, stopPolling, loadDetail, startPolling, importReport]
    )

    const handleImport = useCallback(async () => {
        if (!importFile) {
            Report.info('No file selected', 'Choose a spreadsheet (.xlsx / .csv) to import.', 'Okay')
            return
        }
        setImporting(true)
        Loading.circle('Importing spreadsheet...')
        try {
            const fd = new FormData()
            fd.append('file', importFile)
            if (importName.trim()) fd.append('name', importName.trim())
            if (allowResend) fd.append('allowResend', 'true')
            const resp = await apiUpload('comms/loc-batches/import', fd)
            if (resp.data?.success) {
                const report = (resp.data.report || {}) as ImportReport
                setImportReport(report)
                setImportReportExpanded(true)
                setCopiedUnknownLenders(false)
                setImportFile(null)
                setImportName('')
                setAllowResend(false)
                if (fileInputRef.current) fileInputRef.current.value = ''
                await loadBatches()
                if (report.batchId) await handleSelectBatch(report.batchId)
            }
        } catch (err: any) {
            Report.failure(
                'Import failed',
                err?.response?.data?.message || 'Could not import the spreadsheet.',
                'Okay'
            )
        } finally {
            Loading.remove()
            setImporting(false)
        }
    }, [importFile, importName, allowResend, loadBatches, handleSelectBatch])

    const handleDeleteBatch = useCallback(() => {
        if (!batch) return
        const totalCounted = statusCounts.reduce((sum, sc) => sum + Number(sc.count || 0), 0)
        const sendSummary =
            totalCounted > 0
                ? statusCounts
                      .map((sc) => `${sc.count} ${sc.status}`)
                      .join(', ')
                : 'no emails yet'
        Confirm.prompt(
            'Delete batch?',
            `Type "delete" to permanently remove "${batch.name}" and every email in it (${sendSummary}). ` +
                'This cannot be undone. Claim references from this batch will become importable again.',
            '',
            'Delete',
            'Cancel',
            async (answer: string) => {
                if (String(answer).trim().toLowerCase() !== 'delete') {
                    Report.info('Not deleted', 'You must type "delete" to confirm.', 'Okay')
                    return
                }
                setActionPending(true)
                Loading.circle('Deleting batch…')
                try {
                    const resp = await api({ url: 'comms/loc-batches/delete', data: { batchId: batch.id } })
                    if (resp.data?.success) {
                        Report.success(
                            'Batch deleted',
                            `Removed ${resp.data.sends ?? 0} email(s) and ${resp.data.jobs ?? 0} job record(s).`,
                            'Okay'
                        )
                        stopPolling()
                        setSelectedBatchId(null)
                        setBatch(null)
                        setStatusCounts([])
                        setActiveJob(null)
                        setPreviewRows([])
                        if (importReport?.batchId === batch.id) setImportReport(null)
                        await loadBatches()
                    }
                } catch (err: any) {
                    if (err?.response?.status === 409) {
                        Report.info(
                            'Cannot delete',
                            err?.response?.data?.message ||
                                'This batch has emails that were sent or are in flight.',
                            'Okay'
                        )
                    } else {
                        Report.failure(
                            'Delete failed',
                            err?.response?.data?.message || 'Could not delete the batch.',
                            'Okay'
                        )
                    }
                } finally {
                    Loading.remove()
                    setActionPending(false)
                }
            },
            () => {
                /* cancelled */
            }
        )
    }, [batch, statusCounts, importReport, loadBatches, stopPolling])

    const handleApprove = useCallback(() => {
        if (!batch) return
        const activeRows = previewRows.filter((r) => r.status !== 'cancelled')
        const notRendered = activeRows.filter((r) => r.status === 'queued' && !r.rendered).length
        const activeTotal = activeRows.length || batch.total_claims
        const warning =
            notRendered > 0
                ? `\n\nWARNING: ${notRendered} LOC email(s) are NOT rendered yet and will not have an attachment when sent.`
                : ''
        Confirm.prompt(
            'Approve batch?',
            `Type "approve" to confirm approving "${batch.name}" (${activeTotal} LOC emails).` + warning,
            '',
            'Approve',
            'Cancel',
            async (answer: string) => {
                if (String(answer).trim().toLowerCase() !== 'approve') {
                    Report.info('Not approved', 'You must type "approve" to confirm.', 'Okay')
                    return
                }
                setActionPending(true)
                Loading.circle('Approving batch...')
                try {
                    const resp = await api({ url: 'comms/loc-batches/approve', data: { batchId: batch.id } })
                    if (resp.data?.success) {
                        Report.success('Batch approved', `Approved by ${resp.data.approvedBy}.`, 'Okay')
                        await loadDetail(batch.id)
                        await loadBatches()
                    }
                } catch (err: any) {
                    Report.failure(
                        'Approval failed',
                        err?.response?.data?.message || 'Could not approve the batch.',
                        'Okay'
                    )
                } finally {
                    Loading.remove()
                    setActionPending(false)
                }
            },
            () => {
                /* cancelled */
            }
        )
    }, [batch, previewRows, loadDetail, loadBatches])

    const handleStartJob = useCallback(
        async (jobType: JobType) => {
            if (!batch) return
            setActionPending(true)
            Loading.circle(`Starting ${jobType}...`)
            try {
                const url = jobType === 'render' ? 'comms/loc-batches/render-start' : 'comms/loc-batches/send-start'
                const resp = await api({ url, data: { batchId: batch.id } })
                if (resp.data?.success) {
                    if (resp.data.total === 0) {
                        await loadDetail(batch.id)
                        await loadBatches()
                        Report.info(
                            `Nothing to ${jobType}`,
                            jobType === 'render'
                                ? 'All LOC emails in this batch are already rendered. There is nothing left to render.'
                                : 'There are no queued LOC emails left to send in this batch.',
                            'Okay'
                        )
                        return
                    }
                    Report.success(
                        `${jobType === 'render' ? 'Render' : 'Send'} started`,
                        `Processing ${resp.data.total} LOC email(s) in the background. ` +
                            `Progress will update here automatically.`,
                        'Okay'
                    )
                    const job = await loadDetail(batch.id)
                    if (isJobActive(job)) startPolling(job.id, batch.id)
                    else if (resp.data.jobId) startPolling(resp.data.jobId, batch.id)
                }
            } catch (err: any) {
                if (err?.response?.status === 409) {
                    Report.info(
                        'Already running',
                        err?.response?.data?.message ||
                            `A ${jobType} job is already running for this batch.`,
                        'Okay'
                    )
                    const job = await loadDetail(batch.id)
                    if (isJobActive(job)) startPolling(job.id, batch.id)
                } else {
                    Report.failure(
                        `Could not start ${jobType}`,
                        err?.response?.data?.message || 'Please try again.',
                        'Okay'
                    )
                }
            } finally {
                Loading.remove()
                setActionPending(false)
            }
        },
        [batch, loadDetail, loadBatches, startPolling]
    )

    const rowCounts = useMemo(() => {
        const counts = {
            total: 0,
            active: 0,
            cancelled: 0,
            skippedDuplicate: 0,
            rendered: 0,
            renderable: 0,
            sendable: 0,
            retryable: 0,
            suppressed: 0,
        }
        for (const row of previewRows) {
            counts.total += 1
            if (row.status === 'cancelled') {
                counts.cancelled += 1
                continue
            }
            if (row.status === 'skipped_duplicate') {
                counts.skippedDuplicate += 1
                continue
            }
            counts.active += 1
            if (row.suppressed) counts.suppressed += 1
            if (row.rendered) counts.rendered += 1
            if (row.status === 'queued' && !row.rendered) counts.renderable += 1
            if (row.status === 'queued' && row.rendered) counts.sendable += 1
            if (RETRYABLE_STATUSES.includes(row.status)) counts.retryable += 1
        }
        return counts
    }, [previewRows])

    const filterCounts = useMemo(() => {
        const counts: Record<FailureFilter, number> = {
            all: 0,
            failures: 0,
            notRendered: 0,
            suppressed: 0,
            cancelled: 0,
            skippedDuplicate: 0,
        }
        for (const row of previewRows) {
            counts.all += 1
            if (matchesFilter(row, 'failures')) counts.failures += 1
            if (matchesFilter(row, 'notRendered')) counts.notRendered += 1
            if (matchesFilter(row, 'suppressed')) counts.suppressed += 1
            if (matchesFilter(row, 'cancelled')) counts.cancelled += 1
            if (matchesFilter(row, 'skippedDuplicate')) counts.skippedDuplicate += 1
        }
        return counts
    }, [previewRows])

    const visibleRows = useMemo(
        () => previewRows.filter((row) => matchesFilter(row, filter)),
        [previewRows, filter]
    )

    const retryableCount = rowCounts.retryable

    const stuckSendingCount = useMemo(
        () => statusCounts.find((sc) => sc.status === 'sending')?.count ?? 0,
        [statusCounts]
    )

    const handleRetryFailed = useCallback(() => {
        if (!batch) return
        Confirm.show(
            'Retry and resend failed emails?',
            `${retryableCount} failed / bounced / suppressed email(s) will be re-queued and resent immediately. ` +
                'Hard-bounce suppressions for the affected lender addresses will be cleared ' +
                '(complaint suppressions are kept). Emails that already sent successfully are not affected.',
            'Retry & send',
            'Cancel',
            async () => {
                setActionPending(true)
                Loading.circle('Re-queuing failed emails...')
                try {
                    const resp = await api({ url: 'comms/loc-batches/retry-failed', data: { batchId: batch.id } })
                    if (resp.data?.success) {
                        const cleared: string[] = resp.data.suppressionCleared || []
                        const still: { email: string; reason: string }[] = resp.data.stillSuppressed || []
                        const sesErrors: { email: string; error: string }[] =
                            resp.data.sesSuppressionErrors || []
                        const sendTotal: number = resp.data.sendTotal || 0
                        Report.success(
                            'Emails re-queued',
                            `${resp.data.retried} email(s) re-queued.` +
                                (cleared.length
                                    ? ` Suppression cleared for: ${cleared.join(', ')}.`
                                    : '') +
                                (still.length
                                    ? ` Still suppressed: ${still
                                          .map((s) => `${s.email} (${s.reason})`)
                                          .join(', ')}.`
                                    : '') +
                                (sesErrors.length
                                    ? ` WARNING: could not clear the AWS SES suppression list for: ${sesErrors
                                          .map((s) => s.email)
                                          .join(', ')} — those may bounce again.`
                                    : '') +
                                (sendTotal > 0
                                    ? ` Sending ${sendTotal} now — progress will update here.`
                                    : ' Nothing was ready to send (render any unrendered emails first).'),
                            'Okay'
                        )
                        const job = await loadDetail(batch.id)
                        await loadBatches()
                        if (resp.data.jobId) startPolling(resp.data.jobId, batch.id)
                        else if (isJobActive(job)) startPolling(job.id, batch.id)
                    }
                } catch (err: any) {
                    Report.failure(
                        'Retry failed',
                        err?.response?.data?.message || 'Could not re-queue the failed emails.',
                        'Okay'
                    )
                } finally {
                    Loading.remove()
                    setActionPending(false)
                }
            },
            () => {
                /* cancelled */
            }
        )
    }, [batch, retryableCount, loadDetail, loadBatches, startPolling])

    const handleRecoverStuck = useCallback(() => {
        if (!batch) return
        Confirm.show(
            'Recover stuck emails?',
            `${stuckSendingCount} email(s) are stuck in "sending" — this usually means a send job crashed or ` +
                'was interrupted mid-flight. Recovering re-queues them so they can be sent again. Only emails ' +
                'that have been stuck for at least 5 minutes are affected, so a live send in progress is safe.',
            'Recover',
            'Cancel',
            async () => {
                setActionPending(true)
                Loading.circle('Recovering stuck emails…')
                try {
                    const resp = await api({ url: 'comms/loc-batches/recover-stuck', data: { batchId: batch.id } })
                    if (resp.data?.success) {
                        Report.success(
                            'Stuck emails recovered',
                            resp.data.recovered > 0
                                ? `${resp.data.recovered} email(s) re-queued. Start send again to deliver them.`
                                : 'No emails had been stuck long enough to recover yet — try again in a minute.',
                            'Okay'
                        )
                        await loadDetail(batch.id)
                        await loadBatches()
                    }
                } catch (err: any) {
                    if (err?.response?.status === 409) {
                        Report.info(
                            'Job running',
                            err?.response?.data?.message ||
                                'A job is running for this batch — wait for it to finish, then recover.',
                            'Okay'
                        )
                    } else {
                        Report.failure(
                            'Recovery failed',
                            err?.response?.data?.message || 'Could not recover the stuck emails.',
                            'Okay'
                        )
                    }
                } finally {
                    Loading.remove()
                    setActionPending(false)
                }
            },
            () => {
                /* cancelled */
            }
        )
    }, [batch, stuckSendingCount, loadDetail, loadBatches])

    const handleReapJobs = useCallback(() => {
        if (!batch) return
        Confirm.show(
            'Reap dead jobs?',
            'If a job shows as running but has made no progress for a while, its runner has probably died. ' +
                'Reaping marks any job with no activity in the last minute as failed so you can start again. ' +
                'A job that is actively progressing is not affected.',
            'Reap',
            'Cancel',
            async () => {
                setActionPending(true)
                Loading.circle('Reaping dead jobs…')
                try {
                    const resp = await api({ url: 'comms/loc-batches/reap-jobs', data: { batchId: batch.id } })
                    if (resp.data?.success) {
                        stopPolling()
                        Report.success(
                            resp.data.reaped > 0 ? 'Dead jobs reaped' : 'Nothing to reap',
                            resp.data.reaped > 0
                                ? `${resp.data.reaped} stuck job(s) marked failed. You can start again now.`
                                : 'No jobs were idle long enough to reap — the running job still looks active.',
                            'Okay'
                        )
                        await loadDetail(batch.id)
                        await loadBatches()
                    }
                } catch (err: any) {
                    Report.failure(
                        'Reap failed',
                        err?.response?.data?.message || 'Could not reap dead jobs.',
                        'Okay'
                    )
                } finally {
                    Loading.remove()
                    setActionPending(false)
                }
            },
            () => {
                /* cancelled */
            }
        )
    }, [batch, stopPolling, loadDetail, loadBatches])

    const runRowAction = useCallback(
        async (
            row: PreviewRow,
            action: string,
            url: string,
            loadingLabel: string,
            onSuccess: (data: any) => void
        ) => {
            if (!batch) return
            setRowActionKey(`${row.id}:${action}`)
            Loading.circle(loadingLabel)
            try {
                const resp = await api({ url, data: { sendId: row.id } })
                if (resp.data?.success) {
                    onSuccess(resp.data)
                    await loadDetail(batch.id)
                    await loadBatches()
                } else if (resp.data?.skipped) {
                    Report.warning(
                        'Could not render',
                        resp.data.reason || 'A required document is missing for this email.',
                        'Okay'
                    )
                    await loadDetail(batch.id)
                } else {
                    Report.failure('Action failed', resp.data?.message || 'The action did not apply.', 'Okay')
                }
            } catch (err: any) {
                if (err?.response?.status === 409) {
                    Report.info(
                        'Job running',
                        err?.response?.data?.message ||
                            'A job is running for this batch — wait for it to finish, then retry.',
                        'Okay'
                    )
                    const job = await loadDetail(batch.id)
                    if (isJobActive(job)) startPolling(job.id, batch.id)
                } else {
                    Report.failure(
                        'Action failed',
                        err?.response?.data?.message || 'The action could not be completed.',
                        'Okay'
                    )
                }
            } finally {
                Loading.remove()
                setRowActionKey(null)
            }
        },
        [batch, loadDetail, loadBatches, startPolling]
    )

    const handleRowRender = useCallback(
        (row: PreviewRow) => {
            runRowAction(row, 'render', 'comms/loc-batches/email-render', 'Rendering email…', () => {
                Report.success('Email rendered', `${row.claimRef} is now rendered and ready to send.`, 'Okay')
            })
        },
        [runRowAction]
    )

    const handleRowSendNow = useCallback(
        (row: PreviewRow) => {
            if (!batch) return
            Confirm.show(
                'Send this email now?',
                `Send the LOC email for ${row.claimRef} to ${row.toEmail} immediately. This delivers a real email.`,
                'Send now',
                'Cancel',
                async () => {
                    setRowActionKey(`${row.id}:send`)
                    Loading.circle('Starting send…')
                    try {
                        const resp = await api({ url: 'comms/loc-batches/email-send', data: { sendId: row.id } })
                        if (resp.data?.success) {
                            if (resp.data.jobId) {
                                Report.success(
                                    'Sending',
                                    `Sending the LOC email for ${row.claimRef} — its status will update here.`,
                                    'Okay'
                                )
                                await loadDetail(batch.id)
                                await loadBatches()
                                startPolling(resp.data.jobId, batch.id)
                            } else {
                                Report.warning(
                                    'Not sent',
                                    `${row.claimRef} was not in a sendable state. Render it first if it is unrendered.`,
                                    'Okay'
                                )
                                await loadDetail(batch.id)
                            }
                        }
                    } catch (err: any) {
                        if (err?.response?.status === 409) {
                            Report.info(
                                'Job running',
                                err?.response?.data?.message ||
                                    'A job is running for this batch — wait for it to finish, then try again.',
                                'Okay'
                            )
                            const job = await loadDetail(batch.id)
                            if (isJobActive(job)) startPolling(job.id, batch.id)
                        } else {
                            Report.failure(
                                'Send failed',
                                err?.response?.data?.message || 'Could not start the send.',
                                'Okay'
                            )
                        }
                    } finally {
                        Loading.remove()
                        setRowActionKey(null)
                    }
                },
                () => {
                    /* cancelled */
                }
            )
        },
        [batch, loadDetail, loadBatches, startPolling]
    )

    const handleRowRetry = useCallback(
        (row: PreviewRow) => {
            if (!batch) return
            const renderedNote = row.rendered
                ? ''
                : ' This email is not rendered yet, so it will be re-queued only — render it before it can send.'
            Confirm.show(
                'Retry and resend?',
                `Re-queue and immediately resend the LOC email for ${row.claimRef} (${row.toEmail}). ` +
                    'Any hard-bounce suppression for this address will be cleared first (complaint suppressions are kept).' +
                    renderedNote,
                'Retry & send',
                'Cancel',
                async () => {
                    setRowActionKey(`${row.id}:retry`)
                    Loading.circle('Retrying email…')
                    try {
                        const resp = await api({ url: 'comms/loc-batches/email-retry', data: { sendId: row.id } })
                        if (!resp.data?.success) {
                            Report.failure(
                                'Retry failed',
                                resp.data?.message || 'Could not re-queue the email.',
                                'Okay'
                            )
                            await loadDetail(batch.id)
                            return
                        }
                        const data = resp.data
                        const suppressionNote = data.suppressionCleared
                            ? ' Suppression was cleared first.'
                            : ''
                        await loadDetail(batch.id)
                        await loadBatches()
                        if (data.jobId) {
                            Report.success(
                                'Retrying',
                                `${row.claimRef} re-queued and sending now${suppressionNote} — its status will update here.`,
                                'Okay'
                            )
                            startPolling(data.jobId, batch.id)
                        } else {
                            Report.warning(
                                'Re-queued — render needed',
                                `${row.claimRef} has been re-queued${suppressionNote} but is not rendered yet. ` +
                                    'Render it, then use "Send now".',
                                'Okay'
                            )
                        }
                    } catch (err: any) {
                        if (err?.response?.status === 409) {
                            Report.info(
                                'Job running',
                                err?.response?.data?.message ||
                                    'A job is running for this batch — wait for it to finish, then retry.',
                                'Okay'
                            )
                            const job = await loadDetail(batch.id)
                            if (isJobActive(job)) startPolling(job.id, batch.id)
                        } else {
                            Report.failure(
                                'Retry failed',
                                err?.response?.data?.message || 'Could not retry the email.',
                                'Okay'
                            )
                        }
                    } finally {
                        Loading.remove()
                        setRowActionKey(null)
                    }
                },
                () => {
                    /* cancelled */
                }
            )
        },
        [batch, loadDetail, loadBatches, startPolling]
    )

    const handleRowCancel = useCallback(
        (row: PreviewRow) => {
            Confirm.show(
                'Cancel this email?',
                `Permanently cancel the LOC email for ${row.claimRef} (${row.toEmail}). ` +
                    'It will not be sent and will be excluded from the batch totals. This cannot be undone.',
                'Cancel email',
                'Keep',
                () => {
                    runRowAction(row, 'cancel', 'comms/loc-batches/email-cancel', 'Cancelling email…', () => {
                        Report.success('Email cancelled', `${row.claimRef} has been cancelled.`, 'Okay')
                    })
                },
                () => {
                    /* kept */
                }
            )
        },
        [runRowAction]
    )

    const handleRowRecover = useCallback(
        (row: PreviewRow) => {
            if (!batch) return
            Confirm.show(
                'Mark as bounced?',
                `${row.claimRef} shows "delivered" but a permanent bounce was recorded — likely a late SES ` +
                    'delivery event that masked the real bounce. Mark it back to "bounced" so it can be ' +
                    'edited, retried, or cancelled?',
                'Mark as bounced',
                'Cancel',
                async () => {
                    setRowActionKey(`${row.id}:recover`)
                    Loading.circle('Marking as bounced…')
                    try {
                        const resp = await api({
                            url: 'comms/loc-batches/email-restore-bounce',
                            data: { sendId: row.id },
                        })
                        if (resp.data?.success) {
                            Report.success(
                                'Marked as bounced',
                                `${row.claimRef} is now "bounced" — you can edit, retry, or cancel it.`,
                                'Okay'
                            )
                            const summary = resp.data.send
                            setPreviewRows((rows) =>
                                rows.map((r) =>
                                    r.id === row.id
                                        ? {
                                              ...r,
                                              status: summary?.status ?? 'bounced',
                                              recoverable: false,
                                          }
                                        : r
                                )
                            )
                            await loadBatches()
                        } else {
                            Report.failure(
                                'Action failed',
                                resp.data?.message || 'Could not mark the email as bounced.',
                                'Okay'
                            )
                            await loadDetail(batch.id)
                        }
                    } catch (err: any) {
                        if (err?.response?.status === 409) {
                            Report.info(
                                'Job running',
                                err?.response?.data?.message ||
                                    'A job is running for this batch — wait for it to finish, then try again.',
                                'Okay'
                            )
                            const job = await loadDetail(batch.id)
                            if (isJobActive(job)) startPolling(job.id, batch.id)
                        } else {
                            Report.failure(
                                'Action failed',
                                err?.response?.data?.message || 'Could not mark the email as bounced.',
                                'Okay'
                            )
                        }
                    } finally {
                        Loading.remove()
                        setRowActionKey(null)
                    }
                },
                () => {
                    /* cancelled */
                }
            )
        },
        [batch, loadDetail, loadBatches, startPolling]
    )

    const submitEmailUpdate = useCallback(
        async (
            row: PreviewRow,
            toEmailRaw: string,
            scope: 'single' | 'lender-batch',
            clearSuppression = false
        ) => {
            if (!batch) return
            const toEmail = String(toEmailRaw || '').trim().toLowerCase()
            if (!EMAIL_REGEX.test(toEmail)) {
                Report.info('Invalid email', 'Enter a valid email address.', 'Okay')
                return
            }
            const lenderSends = row.lenderName
                ? previewRows.filter(
                      (r) => r.lenderName === row.lenderName && EDITABLE_STATUSES.includes(r.status)
                  )
                : [row]
            setRowActionKey(`${row.id}:edit`)
            Loading.circle('Updating email…')
            try {
                const resp = await api({
                    url: 'comms/loc-batches/email-update',
                    data: { sendId: row.id, toEmail, scope, clearSuppression },
                })
                if (resp.data?.success) {
                    const warning = resp.data.suppressionWarning
                    if (warning) {
                        Loading.remove()
                        setRowActionKey(null)
                        const reason = warning.reason || 'suppressed'
                        const notes = warning.notes ? ` Notes: ${warning.notes}.` : ''
                        Confirm.show(
                            'Address is suppressed',
                            `${warning.email} is on the suppression list (${reason}).${notes} ` +
                                'Clear the suppression and continue updating the recipient address?',
                            'Clear & continue',
                            'Cancel',
                            () => {
                                submitEmailUpdate(row, toEmail, scope, true)
                            },
                            () => undefined
                        )
                        return
                    }
                    if (resp.data.sesSuppressionError) {
                        Report.warning(
                            'Suppression cleared with a warning',
                            `Could not clear the AWS SES suppression list for ${toEmail} — it may bounce again. ` +
                                `(${resp.data.sesSuppressionError})`,
                            'Okay'
                        )
                    }
                    const suppressionNote = resp.data.suppressionCleared
                        ? ' Suppression was cleared first.'
                        : ''
                    const updated = resp.data.updated ?? 0
                    const rendered = resp.data.rendered ?? 0
                    const skipped = resp.data.skipped || []
                    const scopeNote =
                        resp.data.scope === 'lender-batch'
                            ? `Updated ${updated} send(s) for ${resp.data.lenderName} to ${toEmail}.`
                            : `${row.claimRef} will now send to ${toEmail}.`
                    const renderNote = rendered
                        ? ` ${rendered} Letter(s) of Complaint re-rendered.`
                        : ''
                    if (skipped.length) {
                        Report.warning(
                            'Updated — render needed',
                            `${scopeNote}${renderNote} ${skipped.length} could not be re-rendered: ` +
                                `${skipped.map((s: any) => s.claimRef).join(', ')}. Render them before sending.`,
                            'Okay'
                        )
                    } else if (updated === 0) {
                        Report.info(
                            'No change',
                            `All matching sends already use that address.${suppressionNote}`,
                            'Okay'
                        )
                    } else {
                        Report.success('Email updated', `${scopeNote}${renderNote}${suppressionNote}`, 'Okay')
                    }
                    const newEmail = String(resp.data.toEmail || toEmail).toLowerCase()
                    const summary = resp.data.send
                    const skippedById = new Map<string, any>(
                        (skipped as any[]).map((s) => [s.sendId, s])
                    )
                    const targetIds = new Set(
                        resp.data.scope === 'lender-batch'
                            ? lenderSends.map((r) => r.id)
                            : [row.id]
                    )
                    setPreviewRows((rows) =>
                        rows.map((r) => {
                            if (r.id === row.id && summary) {
                                return {
                                    ...r,
                                    toEmail: summary.toEmail ?? newEmail,
                                    status: summary.status ?? r.status,
                                    rendered: summary.rendered ?? r.rendered,
                                    lastError: summary.lastError ?? null,
                                }
                            }
                            if (!targetIds.has(r.id)) return r
                            if (r.toEmail.toLowerCase() === newEmail) return r
                            const skip = skippedById.get(r.id)
                            if (skip) {
                                return {
                                    ...r,
                                    toEmail: newEmail,
                                    lastError: skip.reason ?? r.lastError,
                                }
                            }
                            return { ...r, toEmail: newEmail, lastError: null }
                        })
                    )
                } else {
                    Report.failure(
                        'Update failed',
                        resp.data?.message || 'The email address was not updated.',
                        'Okay'
                    )
                    await loadDetail(batch.id)
                }
            } catch (err: any) {
                if (err?.response?.status === 409) {
                    Report.info(
                        'Job running',
                        err?.response?.data?.message ||
                            'A job is running for this batch — wait for it to finish, then try again.',
                        'Okay'
                    )
                    const job = await loadDetail(batch.id)
                    if (isJobActive(job)) startPolling(job.id, batch.id)
                } else {
                    Report.failure(
                        'Update failed',
                        err?.response?.data?.message || 'Could not update the email address.',
                        'Okay'
                    )
                }
            } finally {
                Loading.remove()
                setRowActionKey(null)
            }
        },
        [batch, previewRows, loadDetail, startPolling]
    )

    const handleRowEditEmail = useCallback(
        (row: PreviewRow) => {
            if (!batch) return

            const lenderSends =
                row.lenderName
                    ? previewRows.filter(
                          (r) => r.lenderName === row.lenderName && EDITABLE_STATUSES.includes(r.status)
                      )
                    : [row]
            const lenderCount = lenderSends.length

            if (lenderCount > 1) {
                // Notiflix dialogs only allow two buttons, so the multi-send case
                // ("Update all" / "Only this one" / "Cancel") uses a custom modal.
                setEditEmailValue(row.toEmail)
                setEditModal({ row, lenderCount })
            } else {
                Confirm.prompt(
                    'Edit lender email',
                    `Update the recipient address for ${row.claimRef}. ` +
                        'If the send is already rendered, its Letter of Complaint will be re-rendered with the new address.',
                    row.toEmail,
                    'Update',
                    'Cancel',
                    (answer: string) => submitEmailUpdate(row, answer, 'single'),
                    () => {
                        /* cancelled */
                    }
                )
            }
        },
        [batch, previewRows, submitEmailUpdate]
    )

    const runEditModalUpdate = (scope: 'single' | 'lender-batch') => {
        if (!editModal) return
        const toEmail = editEmailValue.trim().toLowerCase()
        if (!EMAIL_REGEX.test(toEmail)) {
            Report.info('Invalid email', 'Enter a valid email address.', 'Okay')
            return
        }
        const { row } = editModal
        setEditModal(null)
        submitEmailUpdate(row, toEmail, scope)
    }

    const handleViewLetter = useCallback(async (sendId: string, kind: AttachmentKind) => {
        setViewingKey(`${sendId}:${kind}`)
        try {
            const resp = await apiDownload('comms/loc-batches/attachment', { sendId, kind })
            const blob = resp.data as Blob
            const url = URL.createObjectURL(blob)
            const opened = window.open(url, '_blank')
            if (!opened) {
                Report.info(
                    'Pop-up blocked',
                    'Allow pop-ups for this site to open rendered documents in a new tab.',
                    'Okay'
                )
            }
            setTimeout(() => URL.revokeObjectURL(url), 60000)
        } catch (err: any) {
            let message = 'Could not open the rendered document.'
            const data = err?.response?.data
            if (data instanceof Blob) {
                try {
                    const text = await data.text()
                    message = JSON.parse(text)?.message || message
                } catch {
                    message = 'Could not open the rendered document.'
                }
            } else if (data?.message) {
                message = data.message
            }
            Report.failure('Could not open document', message, 'Okay')
        } finally {
            setViewingKey(null)
        }
    }, [])

    const handleExportSends = useCallback(async () => {
        if (!batch) return
        setExporting(true)
        Loading.circle('Building export…')
        try {
            const resp = await apiDownload('comms/loc-batches/export', { batchId: batch.id })
            const blob = resp.data as Blob
            const disposition = resp.headers?.['content-disposition'] || ''
            const match = /filename\*?=(?:UTF-8'')?"?([^"\n;]+)"?/i.exec(disposition)
            const fallback = `${batch.name.replace(/[^\w.-]+/g, '_')}-sends-${new Date()
                .toISOString()
                .slice(0, 10)}.xlsx`
            const filename = match ? decodeURIComponent(match[1]) : fallback
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = filename
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setTimeout(() => URL.revokeObjectURL(url), 60000)
        } catch (err: any) {
            let message = 'Could not export the sends.'
            const data = err?.response?.data
            if (data instanceof Blob) {
                try {
                    message = JSON.parse(await data.text())?.message || message
                } catch {
                    message = 'Could not export the sends.'
                }
            } else if (data?.message) {
                message = data.message
            }
            Report.failure('Export failed', message, 'Okay')
        } finally {
            Loading.remove()
            setExporting(false)
        }
    }, [batch])

    const handleRefresh = useCallback(async () => {
        await loadBatches()
        if (selectedBatchId) {
            const job = await loadDetail(selectedBatchId)
            if (isJobActive(job) && !pollRef.current) startPolling(job.id, selectedBatchId)
        }
    }, [loadBatches, loadDetail, selectedBatchId, startPolling])

    const showImportReport =
        !!importReport && !!selectedBatchId && importReport.batchId === selectedBatchId

    const handleCopyUnknownLenders = useCallback(async () => {
        if (!importReport?.unknownLenders?.length) return
        try {
            await navigator.clipboard.writeText(importReport.unknownLenders.join('\n'))
            setCopiedUnknownLenders(true)
            setTimeout(() => setCopiedUnknownLenders(false), 1600)
        } catch {
            Report.info(
                'Copy failed',
                'Select and copy the list manually — clipboard access was blocked.',
                'Okay'
            )
        }
    }, [importReport])

    const jobIsActive = isJobActive(activeJob)
    const renderJobActive = jobIsActive && activeJob?.job_type === 'render'
    const sendJobActive = jobIsActive && activeJob?.job_type === 'send'

    const renderableCount = rowCounts.renderable
    const sendableCount = rowCounts.sendable
    const allRendered = rowCounts.active > 0 && renderableCount === 0
    const batchIsTerminal = !!batch && (batch.status === 'sent' || batch.status === 'cancelled')
    const canStartRender = !!batch && !batchIsTerminal && renderableCount > 0 && !jobIsActive
    const canStartSend =
        !!batch &&
        (batch.status === 'approved' || batch.status === 'sending') &&
        sendableCount > 0 &&
        !jobIsActive

    const sortedStatusCounts = useMemo(
        () => [...statusCounts].sort((a, b) => b.count - a.count),
        [statusCounts]
    )

    const hasSentRows = useMemo(
        () =>
            statusCounts.some(
                (sc) =>
                    ['sent', 'delivered', 'bounced', 'complained'].includes(sc.status) &&
                    sc.count > 0
            ),
        [statusCounts]
    )

    const importStats: { label: string; value: number; tone: 'default' | 'good' | 'bad' | 'warn' }[] = importReport
        ? [
              { label: 'Queued', value: importReport.queued ?? 0, tone: importReport.queued ? 'good' : 'default' },
              { label: 'Invalid', value: importReport.counts?.invalid ?? 0, tone: importReport.counts?.invalid ? 'bad' : 'default' },
              { label: 'Dup in file', value: importReport.counts?.duplicateInFile ?? 0, tone: importReport.counts?.duplicateInFile ? 'warn' : 'default' },
              { label: 'Already in DB', value: importReport.counts?.alreadyInDb ?? 0, tone: importReport.counts?.alreadyInDb ? 'warn' : 'default' },
              { label: 'Skipped', value: importReport.counts?.skippedDuplicates ?? 0, tone: 'default' },
              { label: 'Suppressed', value: importReport.counts?.suppressedWarnings ?? 0, tone: importReport.counts?.suppressedWarnings ? 'warn' : 'default' },
              { label: 'Unknown lenders', value: importReport.counts?.unknownLenders ?? 0, tone: importReport.counts?.unknownLenders ? 'bad' : 'default' },
          ]
        : []

    const filterOptions = FILTER_CHIPS.map(({ key, label }) => {
        const count = key === 'all' ? filterCounts.all : filterCounts[key]
        return { value: key, label, count, disabled: key !== 'all' && count === 0 }
    })
    const segmentValue: FailureFilter = filter.startsWith('status:') ? 'all' : (filter as FailureFilter)

    return (
        <>
            <div className={styles.layout}>
                <aside className={styles.masterColumn}>
                    <Card as="section" title="Import batch" padding="sm">
                        <div className={styles.stack} style={{ gap: 12 }}>
                            <Field label="Spreadsheet" hint=".xlsx, .xls or .csv">
                                <FileInput ref={fileInputRef} accept=".xlsx,.xls,.csv" block fileName={importFile?.name} onChange={(files) => setImportFile(files?.[0] || null)} disabled={importing} />
                            </Field>
                            <Field label="Batch name" optional>
                                {(id) => <Input id={id} placeholder="Defaults to the file name" value={importName} onChange={(e) => setImportName(e.target.value)} disabled={importing} />}
                            </Field>
                            <Checkbox label="Allow resend" hint="Include claim refs already sent in a previous batch" checked={allowResend} onChange={(e) => setAllowResend(e.target.checked)} disabled={importing} />
                            <Button variant="primary" onClick={handleImport} disabled={importing || !importFile} loading={importing} block>
                                Import
                            </Button>
                        </div>
                    </Card>

                    <div className={styles.listHeader}>
                        <h3 className={styles.listTitle}>Batches</h3>
                        <Button size="sm" variant="ghost" onClick={handleRefresh}>
                            Refresh
                        </Button>
                    </div>

                    {listLoading ? (
                        <Spinner block text="Loading batches…" />
                    ) : batches.length === 0 ? (
                        <EmptyState bare title="No batches yet" description="Import a spreadsheet to begin." />
                    ) : (
                        <ul className={styles.batchList}>
                            {batches.map((b) => (
                                <li key={b.id} className={`${styles.batchItem} ${b.id === selectedBatchId ? styles.batchItemActive : ''}`} onClick={() => handleSelectBatch(b.id)}>
                                    <div className={styles.batchItemTop}>
                                        <span className={styles.batchItemName}>{b.name}</span>
                                        <BatchStatusBadge status={b.status} />
                                    </div>
                                    <div className={styles.batchItemMeta}>
                                        <span>{b.total_claims} claims</span>
                                        <span>{formatDateTime(b.created_at)}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </aside>

                <section className={styles.detailColumn}>
                    {!selectedBatchId ? (
                        <EmptyState bare title="Select a batch" description="Pick a batch from the list to view its emails and progress." />
                    ) : detailLoading || !batch ? (
                        <Spinner block text="Loading batch…" />
                    ) : (
                        <div className={styles.stack}>
                            <div className={styles.detailHeader}>
                                <div>
                                    <h2 className={styles.detailTitle}>{batch.name}</h2>
                                    <div className={styles.detailSub}>
                                        <BatchStatusBadge status={batch.status} />
                                        <span>Created by {batch.created_by}</span>
                                        <span>{formatDateTime(batch.created_at)}</span>
                                        {batch.approved_by && (
                                            <span>
                                                Approved by {batch.approved_by} · {formatDateTime(batch.approved_at)}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className={styles.detailHeaderActions}>
                                    <Button size="sm" onClick={handleRefresh}>
                                        Refresh
                                    </Button>
                                    {DELETABLE_BATCH_STATUSES.includes(batch.status) && !jobIsActive && (
                                        <Button size="sm" variant="danger" onClick={handleDeleteBatch} disabled={actionPending} title="Permanently delete this batch and every email in it">
                                            Delete batch
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {showImportReport && importReport && (
                                <Card
                                    as="section"
                                    muted
                                    padding="sm"
                                    title="Import result"
                                    subtitle={
                                        importReport.sourceFile
                                            ? `${importReport.sourceFile}${importReport.sheetName ? ` · sheet "${importReport.sheetName}"` : ''}${typeof importReport.totalRows === 'number' ? ` · ${importReport.totalRows} row(s) read` : ''}`
                                            : `${importReport.totalRows ?? 0} row(s) read`
                                    }
                                    actions={
                                        <>
                                            <Button size="sm" variant="ghost" onClick={() => setImportReportExpanded((v) => !v)}>
                                                {importReportExpanded ? 'Collapse' : 'Expand'}
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => setImportReport(null)} title="Dismiss until the next import">
                                                Dismiss
                                            </Button>
                                        </>
                                    }
                                >
                                    <div className={styles.stack}>
                                        <StatGrid minColumn={120}>
                                            {importStats.map((s) => (
                                                <StatCard key={s.label} compact label={s.label} value={s.value} tone={s.tone} />
                                            ))}
                                        </StatGrid>

                                        {importReport.unknownLenders?.length > 0 && (
                                            <Banner
                                                tone="warn"
                                                title={`Unknown lenders (${importReport.unknownLenders.length})`}
                                                actions={
                                                    <Button size="sm" onClick={handleCopyUnknownLenders}>
                                                        {copiedUnknownLenders ? 'Copied' : 'Copy'}
                                                    </Button>
                                                }
                                            >
                                                These names in your file don&apos;t match any entry in the lender directory, so every row for them was dropped. Add them to the directory (or fix the spelling in the spreadsheet) and re-import.
                                                <ul className={styles.unknownLendersList}>
                                                    {importReport.unknownLenders.map((name) => (
                                                        <li key={name}>{name}</li>
                                                    ))}
                                                </ul>
                                            </Banner>
                                        )}

                                        {importReportExpanded && <ImportRejectSections report={importReport} />}
                                    </div>
                                </Card>
                            )}

                            {previewRows.length > 0 && (
                                <StatGrid minColumn={120}>
                                    <StatCard compact label="Emails" value={rowCounts.active} />
                                    <StatCard compact label="Rendered" value={rowCounts.rendered} tone={rowCounts.rendered && rowCounts.rendered === rowCounts.active ? 'good' : 'default'} />
                                    <StatCard compact label="Not rendered" value={renderableCount} tone={renderableCount ? 'warn' : 'default'} />
                                    <StatCard compact label="Suppressed" value={rowCounts.suppressed} tone={rowCounts.suppressed ? 'bad' : 'default'} />
                                    {rowCounts.cancelled > 0 && <StatCard compact label="Cancelled" value={rowCounts.cancelled} tone="muted" />}
                                    {rowCounts.skippedDuplicate > 0 && <StatCard compact label="Skipped" value={rowCounts.skippedDuplicate} tone="muted" />}
                                </StatGrid>
                            )}

                            {sortedStatusCounts.length > 0 && (
                                <div className={styles.chips}>
                                    {sortedStatusCounts.map((sc) => {
                                        const chipFilter: PreviewFilter = `status:${sc.status}`
                                        const active = filter === chipFilter
                                        return (
                                            <button key={sc.status} type="button" className={`${styles.chip} ${active ? styles.chipActive : ''}`} onClick={() => setFilter(active ? 'all' : chipFilter)} title={active ? 'Clear this filter' : `Show only "${sc.status}" rows`} aria-pressed={active}>
                                                {sc.status}: {sc.count}
                                            </button>
                                        )
                                    })}
                                </div>
                            )}

                            {jobIsActive && activeJob && (
                                <Card muted padding="sm">
                                    <ProgressBar value={activeJob.percent} label={`${activeJob.job_type === 'render' ? 'Rendering' : 'Sending'} — ${activeJob.status}`} tone={activeJob.failed > 0 ? 'warn' : 'accent'} />
                                    <div className={styles.jobFooter}>
                                        <span>
                                            {activeJob.processed} / {activeJob.total} processed
                                            {activeJob.failed > 0 && (
                                                <>
                                                    {' · '}
                                                    <StatusPill tone="bad" size="sm">
                                                        {activeJob.failed} failed
                                                    </StatusPill>
                                                </>
                                            )}
                                        </span>
                                        <Button size="sm" variant="link" onClick={handleReapJobs} disabled={actionPending} title="If this job looks stuck, mark it failed so you can start again">
                                            Reap dead jobs
                                        </Button>
                                    </div>
                                </Card>
                            )}

                            <div className={styles.actions}>
                                {(canStartRender || renderJobActive) && (
                                    <Button variant={batch.status === 'draft' ? 'neutral' : 'primary'} onClick={() => handleStartJob('render')} disabled={actionPending || jobIsActive} loading={renderJobActive}>
                                        Start render{renderableCount ? ` (${renderableCount})` : ''}
                                    </Button>
                                )}
                                {batch.status === 'draft' && (
                                    <>
                                        <Button variant="primary" onClick={handleApprove} disabled={actionPending || jobIsActive}>
                                            Approve
                                        </Button>
                                        {allRendered && <span className={styles.note}>All {sendableCount} LOC email(s) already rendered — ready to approve.</span>}
                                    </>
                                )}
                                {(canStartSend || sendJobActive) && (
                                    <Button variant="primary" onClick={() => handleStartJob('send')} disabled={actionPending || jobIsActive} loading={sendJobActive}>
                                        Start send{sendableCount ? ` (${sendableCount})` : ''}
                                    </Button>
                                )}
                                {batch.status !== 'draft' && retryableCount > 0 && (
                                    <Button onClick={handleRetryFailed} disabled={actionPending || jobIsActive}>
                                        Retry {retryableCount} failed
                                    </Button>
                                )}
                                {stuckSendingCount > 0 && !jobIsActive && (
                                    <Button onClick={handleRecoverStuck} disabled={actionPending} title="Re-queue emails stuck in 'sending' after an interrupted job">
                                        Recover {stuckSendingCount} stuck
                                    </Button>
                                )}
                                {hasSentRows && (
                                    <Button onClick={handleExportSends} disabled={exporting || actionPending || jobIsActive} loading={exporting}>
                                        Export sends
                                    </Button>
                                )}
                                {!canStartRender && !canStartSend && !jobIsActive && batch.status !== 'draft' && retryableCount === 0 && stuckSendingCount === 0 && <span className={styles.note}>Read-only — use Refresh to update delivery counts.</span>}
                            </div>

                            <div className={styles.stack} style={{ gap: 10 }}>
                                <h3 className={styles.sectionTitle}>Preview</h3>
                                {previewRows.length > 0 && (
                                    <div className={styles.filterBar}>
                                        <SegmentedControl<FailureFilter> value={segmentValue} onChange={(v) => setFilter(v)} options={filterOptions} />
                                        {filter !== 'all' && (
                                            <Button size="sm" variant="link" onClick={() => setFilter('all')}>
                                                Clear filter
                                            </Button>
                                        )}
                                    </div>
                                )}
                                {previewRows.length === 0 ? (
                                    <EmptyState bare title="No LOC emails in this batch" />
                                ) : visibleRows.length === 0 ? (
                                    <EmptyState
                                        bare
                                        title="No rows match this filter"
                                        action={
                                            <Button size="sm" onClick={() => setFilter('all')}>
                                                Clear filter
                                            </Button>
                                        }
                                    />
                                ) : (
                                    <div className={styles.tableWrapper}>
                                        <table className={styles.previewTable}>
                                            <thead>
                                                <tr>
                                                    <th>Claim ref</th>
                                                    <th>Lender</th>
                                                    <th>To</th>
                                                    <th>Footer ref</th>
                                                    <th>Status</th>
                                                    <th>Rendered</th>
                                                    <th>Documents</th>
                                                    <th>Suppression</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleRows.map((row) => {
                                                    const isCancelled = row.status === 'cancelled'
                                                    const isSkipped = row.status === 'skipped_duplicate'
                                                    const skippedOriginTitle = isSkipped ? [row.originalBatchName ? `Original batch: ${row.originalBatchName}` : null, row.originalSentAt ? `Sent: ${formatDateTime(row.originalSentAt)}` : null].filter(Boolean).join(' • ') : ''
                                                    const rowBusy = rowActionKey !== null || jobIsActive
                                                    const canRenderRow = !row.rendered && row.status === 'queued'
                                                    const canSendRow = row.status === 'queued' && row.rendered && (batch.status === 'approved' || batch.status === 'sending')
                                                    const canRetryRow = RETRYABLE_STATUSES.includes(row.status)
                                                    const canCancelRow = CANCELLABLE_STATUSES.includes(row.status)
                                                    const canEditRow = EDITABLE_STATUSES.includes(row.status)
                                                    const canRecoverRow = row.recoverable === true && !jobIsActive
                                                    const rowClass = [isCancelled ? styles.cancelledRow : '', isSkipped ? styles.skippedRow : '', !isCancelled && !isSkipped && row.suppressed ? styles.suppressedRow : ''].filter(Boolean).join(' ')
                                                    const pendingFor = (action: string) => rowActionKey === `${row.id}:${action}`
                                                    return (
                                                        <tr key={row.id} className={rowClass || undefined}>
                                                            <td className={styles.mono}>{row.claimRef}</td>
                                                            <td>{row.lenderName || '—'}</td>
                                                            <td className={styles.mono}>{row.toEmail}</td>
                                                            <td className={styles.mono}>{row.footerRef}</td>
                                                            <td>
                                                                <div title={isSkipped ? skippedOriginTitle : undefined}>{isSkipped ? 'skipped' : row.status}</div>
                                                                {row.lastError && (
                                                                    <div className={styles.rowError} title={isSkipped && skippedOriginTitle ? `${row.lastError} — ${skippedOriginTitle}` : row.lastError}>
                                                                        {row.lastError}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className={row.rendered ? styles.flagOk : styles.flagWarn}>{row.rendered ? 'yes' : 'no'}</td>
                                                            <td>
                                                                {row.rendered ? (
                                                                    <span className={styles.docLinks}>
                                                                        {ATTACHMENT_LINKS.map(({ kind, label }) => {
                                                                            const key = `${row.id}:${kind}`
                                                                            return (
                                                                                <Button key={kind} size="sm" variant="link" onClick={() => handleViewLetter(row.id, kind)} disabled={viewingKey === key} loading={viewingKey === key}>
                                                                                    {label}
                                                                                </Button>
                                                                            )
                                                                        })}
                                                                    </span>
                                                                ) : (
                                                                    <span className={styles.muted}>—</span>
                                                                )}
                                                            </td>
                                                            <td className={row.suppressed ? styles.flagBad : styles.flagOk} title={row.suppressionNotes || ''}>
                                                                {row.suppressed ? `SUPPRESSED (${row.suppressionReason})` : 'clear'}
                                                            </td>
                                                            <td>
                                                                {isCancelled ? (
                                                                    <span className={styles.muted}>cancelled</span>
                                                                ) : isSkipped ? (
                                                                    <span className={styles.muted} title={skippedOriginTitle || undefined}>
                                                                        —
                                                                    </span>
                                                                ) : (
                                                                    <span className={styles.rowActions}>
                                                                        {canRenderRow && (
                                                                            <Button size="sm" onClick={() => handleRowRender(row)} disabled={rowBusy} loading={pendingFor('render')}>
                                                                                Render
                                                                            </Button>
                                                                        )}
                                                                        {canSendRow && (
                                                                            <Button size="sm" onClick={() => handleRowSendNow(row)} disabled={rowBusy} loading={pendingFor('send')}>
                                                                                Send now
                                                                            </Button>
                                                                        )}
                                                                        {canRetryRow && (
                                                                            <Button size="sm" onClick={() => handleRowRetry(row)} disabled={rowBusy} loading={pendingFor('retry')}>
                                                                                Retry
                                                                            </Button>
                                                                        )}
                                                                        {canEditRow && (
                                                                            <Button size="sm" onClick={() => handleRowEditEmail(row)} disabled={rowBusy} loading={pendingFor('edit')}>
                                                                                Edit email
                                                                            </Button>
                                                                        )}
                                                                        {canCancelRow && (
                                                                            <Button size="sm" variant="danger" onClick={() => handleRowCancel(row)} disabled={rowBusy} loading={pendingFor('cancel')}>
                                                                                Cancel
                                                                            </Button>
                                                                        )}
                                                                        {canRecoverRow && (
                                                                            <Button size="sm" variant="danger" onClick={() => handleRowRecover(row)} disabled={rowBusy} loading={pendingFor('recover')}>
                                                                                Mark as bounced
                                                                            </Button>
                                                                        )}
                                                                        {!canRenderRow && !canSendRow && !canRetryRow && !canEditRow && !canCancelRow && !canRecoverRow && <span className={styles.muted}>—</span>}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </section>
            </div>

            <Modal
                open={editModal !== null}
                onClose={() => setEditModal(null)}
                title="Edit lender email"
                subtitle={editModal ? `${editModal.row.lenderName} · ${editModal.lenderCount} editable send(s) in this batch` : undefined}
                size="sm"
                footer={
                    <>
                        <Button onClick={() => setEditModal(null)}>Cancel</Button>
                        <Button onClick={() => runEditModalUpdate('single')}>Only this one</Button>
                        <Button variant="primary" onClick={() => runEditModalUpdate('lender-batch')}>
                            Update all {editModal?.lenderCount ?? ''}
                        </Button>
                    </>
                }
            >
                <p className={styles.note} style={{ margin: '0 0 12px' }}>
                    Enter the new recipient address, then choose whether to update only this send or all of them. This does not change the lender directory or any other batch. Rendered sends are re-rendered with the new address.
                </p>
                <Field label="New recipient address" required>
                    {(id) => <Input id={id} type="email" value={editEmailValue} onChange={(e) => setEditEmailValue(e.target.value)} placeholder="name@lender.co.uk" autoFocus />}
                </Field>
            </Modal>
        </>
    )
}
